import { CHUNK_SIZE, COLUMN_HEIGHT, SEA_LEVEL, SUN_DIRECTION, WORLD_EXTENT } from "../sim/world/constants";
import { blockIndex, PaletteSubChunk } from "../sim/world/palette";
import { CAVE_PASS_CODE, createCaveSampler } from "./caves";
import { SURFACE_EROSION_RANGE } from "./constants";
import { WORLDGEN_BLOCK_ID } from "./blocks";
import { alphaRegionGraph } from "./regions";
import { hash32, hashFloat, valueNoise2 } from "./random";
import { placedStructures, stampStructures } from "./structures";
import { sampleTerrain, surfaceErosionAt } from "./terrain";
import type { BiomeId } from "./types";
import {
  boulderOccupies,
  bouldersNear,
  flowerAt,
  flowerHeadHeight,
  groundCoverHeight,
  MAX_BOULDER_REACH,
  MAX_GROUND_COVER_HEIGHT,
  stampTrees,
  treesNear,
} from "./vegetation";

/** Ground-plane travel per voxel of height, toward the sun. */
const SUN_STEP_X = SUN_DIRECTION.x / SUN_DIRECTION.y;
const SUN_STEP_Z = SUN_DIRECTION.z / SUN_DIRECTION.y;
/** Reach and stride of the terrain sun march, in voxels. */
const SHADOW_MARCH = 48;
const SHADOW_STEP = 3;

const AIR = 0;
const STONE = 1;
const DIRT = 2;
const GRASS = 3;
const WATER = 5;
const SAND = WORLDGEN_BLOCK_ID.sand;
const PLAINS_GRASS = WORLDGEN_BLOCK_ID.plainsGrass;
const FOREST_GROUND_COVER = WORLDGEN_BLOCK_ID.forestGroundCover;

function assertChunkCoordinate(cx: number, cy: number, cz: number): void {
  const columns = WORLD_EXTENT / CHUNK_SIZE;
  if (!Number.isInteger(cx) || !Number.isInteger(cy) || !Number.isInteger(cz)
    || cx < 0 || cx >= columns || cz < 0 || cz >= columns || cy < 0 || cy >= COLUMN_HEIGHT) {
    throw new RangeError("worldgen chunk coordinate is outside the finite world");
  }
}

/**
 * Pure sub-chunk generation. Every dependency is reconstructed from the input;
 * callers may freely mutate the returned storage without affecting later runs.
 */
export function generate(seed: number, cx: number, cy: number, cz: number): PaletteSubChunk {
  assertChunkCoordinate(cx, cy, cz);
  if (!Number.isFinite(seed)) throw new RangeError("world seed must be finite");
  const normalizedSeed = seed >>> 0;
  const graph = alphaRegionGraph(normalizedSeed);
  const placements = placedStructures(normalizedSeed, graph);
  const chunk = new PaletteSubChunk(AIR);
  const protectedBlocks = new Uint8Array(CHUNK_SIZE ** 3);
  const surfaces = new Int16Array(CHUNK_SIZE * CHUNK_SIZE);
  const cliffColumns = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
  /**
   * The surface as actually built, per column. Distinct from `surfaces`, which
   * stays smooth for the cave and structure contracts: skylight has to follow
   * the real top face, or every column the erosion pushed *down* falls below
   * the smooth height, keeps ambient-only light, and renders as a dark blotch
   * the exact shape of the erosion noise.
   */
  const litSurfaces = new Int16Array(CHUNK_SIZE * CHUNK_SIZE);
  /** 1 where the surface came out bare stone, so nothing plants there. */
  const rockColumns = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
  const worldMinY = cy * CHUNK_SIZE;
  const worldMinX = cx * CHUNK_SIZE;
  const worldMinZ = cz * CHUNK_SIZE;
  const caveSampler = createCaveSampler(
    normalizedSeed,
    worldMinX,
    worldMinX + CHUNK_SIZE - 1,
    worldMinZ,
    worldMinZ + CHUNK_SIZE - 1,
  );

  /**
   * Footprints of authored structures overlapping this chunk, in world XZ.
   *
   * Prefabs are conformed to the *smooth* surface, so their columns must not be
   * eroded — otherwise a couple of voxels of terrain bank intrudes into the
   * base course and the structure reads as half-buried. A rotated prefab's
   * footprint is bounded by its longest horizontal side, which is what the max
   * below covers without having to reproduce the rotation maths here.
   */
  const structurePads = placements
    .map((placement) => {
      const span = Math.max(placement.prefab.size[0], placement.prefab.size[2]);
      return { minX: placement.originX, minZ: placement.originZ, maxX: placement.originX + span, maxZ: placement.originZ + span };
    })
    .filter((pad) => pad.maxX >= worldMinX && pad.minX <= worldMinX + CHUNK_SIZE - 1
      && pad.maxZ >= worldMinZ && pad.minZ <= worldMinZ + CHUNK_SIZE - 1);

  const onStructurePad = (worldX: number, worldZ: number): boolean => structurePads.some(
    (pad) => worldX >= pad.minX && worldX <= pad.maxX && worldZ >= pad.minZ && worldZ <= pad.maxZ,
  );

  /**
   * Surface heights over the chunk plus a one-voxel margin and the band the sun
   * march reaches into, sampled once.
   *
   * Serves two readers. The sun march needs the up-sun band: doing it per
   * column per step costs sixteen thousand terrain samples a chunk and tripled
   * generation time. The surface classifier needs the one-voxel margin, so it
   * can take a slope from the neighbours of every column in the chunk.
   */
  const BAND_MARGIN = 3;
  const bandW = CHUNK_SIZE + BAND_MARGIN + Math.ceil(SHADOW_MARCH * SUN_STEP_X) + 2;
  const bandD = CHUNK_SIZE + BAND_MARGIN + Math.ceil(SHADOW_MARCH * SUN_STEP_Z) + 2;
  const band = new Int16Array(bandW * bandD);
  /**
   * The same band without the erosion, for the slope classifier alone.
   *
   * Both come out of one `sampleTerrain` call per column, so the second band is
   * free — and the two readers genuinely want different surfaces. The sun march
   * must shade the ground as built, or a bank throws no shadow. The classifier
   * must not: it is asking how steep the *terrain* is, and erosion is roughness,
   * not gradient. Measured on the eroded band, a few voxels of surface noise are
   * indistinguishable from a real slope, so bare rock breaks out in speckles
   * across level ground.
   */
  const smoothBand = new Int16Array(bandW * bandD);
  for (let bz = 0; bz < bandD; bz += 1) {
    for (let bx = 0; bx < bandW; bx += 1) {
      const sampleX = worldMinX - BAND_MARGIN + bx;
      const sampleZ = worldMinZ - BAND_MARGIN + bz;
      const index = bx + bz * bandW;
      if (sampleX < 0 || sampleX >= WORLD_EXTENT || sampleZ < 0 || sampleZ >= WORLD_EXTENT) {
        band[index] = -1;
        smoothBand[index] = -1;
        continue;
      }
      const sample = sampleTerrain(normalizedSeed, sampleX, sampleZ, graph);
      smoothBand[index] = sample.height;
      band[index] = sample.height + surfaceErosionAt(normalizedSeed, sampleX, sampleZ, sample);
    }
  }
  /** Height at a chunk-local position, or undefined outside the world. */
  const sampleBand = (source: Int16Array, localX: number, localZ: number): number | undefined => {
    const ix = Math.round(localX) + BAND_MARGIN;
    const iz = Math.round(localZ) + BAND_MARGIN;
    if (ix < 0 || ix >= bandW || iz < 0 || iz >= bandD) return undefined;
    const value = source[ix + iz * bandW] ?? -1;
    return value < 0 ? undefined : value;
  };
  const bandHeight = (localX: number, localZ: number): number | undefined => sampleBand(band, localX, localZ);

  /**
   * Steepness of a column, in voxels of rise per voxel travelled.
   *
   * This is what decides rock from grass. In the references a hillside is bare
   * layered stone wherever it is steep and grass wherever it is not — the two
   * are never mixed arbitrarily, and a world with grass draped over every slope
   * regardless of angle reads as a golf course.
   */
  const slopeAt = (localX: number, localZ: number): number => {
    const here = sampleBand(smoothBand, localX, localZ);
    if (here === undefined) return 0;
    let steepest = 0;
    // Over three voxels rather than one: the height field is quantised to whole
    // voxels, so a single-column baseline reads 0 or 1 on any slope short of 45
    // degrees and cannot tell them apart.
    for (const [dx, dz] of [[3, 0], [-3, 0], [0, 3], [0, -3]] as const) {
      const neighbour = sampleBand(smoothBand, localX + dx, localZ + dz);
      if (neighbour === undefined) continue;
      steepest = Math.max(steepest, Math.abs(here - neighbour));
    }
    return steepest;
  };

  for (let z = 0; z < CHUNK_SIZE; z += 1) {
    for (let x = 0; x < CHUNK_SIZE; x += 1) {
      const worldX = worldMinX + x;
      const worldZ = worldMinZ + z;
      const terrain = sampleTerrain(normalizedSeed, worldX, worldZ, graph);
      const groundCover = terrain.biome === "forest"
        && hash32(normalizedSeed ^ 0x4f1b_77a3, worldX, 0, worldZ) % 11 === 0
        && caveSampler.passAt(worldX, terrain.height, worldZ, terrain.height) !== "entrance";
      const columnIndex = x + z * CHUNK_SIZE;
      // `surfaces` stays the *smooth* height. Caves, structure conformance and
      // the terrain-continuity invariant are all defined against it; only the
      // blocks actually written below carry the erosion.
      surfaces[columnIndex] = terrain.height;
      cliffColumns[columnIndex] = terrain.edgeCliff ? 1 : 0;
      const filledHeight = onStructurePad(worldX, worldZ)
        ? terrain.height
        : terrain.height + surfaceErosionAt(normalizedSeed, worldX, worldZ, terrain);
      litSurfaces[columnIndex] = filledHeight;
      // Steep ground is bare stone; gentle ground grows. A little noise on the
      // threshold keeps the boundary from tracing a clean contour, and scatters
      // outcrops onto flatter ground the way the references do.
      /*
       * Bare ground. The references are never wall-to-wall turf: there is
       * trodden dirt and loose stone between the grass, and that is what gives
       * a meadow anything to read against. One broad noise field decides it,
       * with a second finer one picking dirt from stone so a patch is not one
       * flat colour either.
       */
      const bareField = valueNoise2(normalizedSeed ^ 0x71c3_5ba9, worldX / 26, worldZ / 26);
      const bare = bareField > 0.34;
      const bareStone = bare && valueNoise2(normalizedSeed ^ 0x2fd8_1e47, worldX / 9, worldZ / 9) > 0.15;
      const steepness = slopeAt(x, z);
      const outcrop = hashFloat(normalizedSeed ^ 0x6c8e_31b7, worldX >> 3, 0, worldZ >> 3);
      // Rise over three voxels. Raised again now the crag octave gives the land
      // real steepness: at a threshold of three the whole hillside came out
      // grey, and the references keep rock to the faces with grass over
      // everything gentler. Outcrops still break through on rolling ground.
      const rocky = steepness >= 4 + outcrop * 3 || (steepness >= 2 && outcrop > 0.87);
      rockColumns[columnIndex] = rocky || bare ? 1 : 0;
      for (let y = 0; y < CHUNK_SIZE; y += 1) {
        const worldY = worldMinY + y;
        let block = AIR;
        if (terrain.edgeCliff || worldY <= filledHeight) {
          if (terrain.edgeCliff || worldY < filledHeight - 3 || terrain.oceanShelf) block = STONE;
          else if (worldY < filledHeight) block = DIRT;
          else if (terrain.biome === "coast") block = SAND;
          // A river bed is washed gravel and sand, never turf.
          else if (terrain.river > 0.25) block = SAND;
          else if (rocky || bareStone) block = STONE;
          else if (bare) block = DIRT;
          else block = terrain.biome === "plains" ? PLAINS_GRASS : GRASS;
        } else if (worldY <= SEA_LEVEL || (terrain.river > 0 && worldY <= terrain.riverSurface)) {
          block = WATER;
        } else if (groundCover && worldY === filledHeight + 1) {
          block = FOREST_GROUND_COVER;
        }
        if (block !== AIR) {
          chunk.set(x, y, z, block);
          // Direct-sky faces receive the sun channel. Deeper faces deliberately
          // retain ambient-only light so cliff shelves and overhangs read in AO.
          if (worldY >= filledHeight - 1 || (block === WATER && worldY === SEA_LEVEL)) {
            chunk.setLight(x, y, z, 0xf000);
          }
        }
      }
    }
  }

  stampStructures(normalizedSeed, graph, placements, chunk, protectedBlocks, cx, cy, cz);

  // A null volume is the sampler's "nothing carved here" signal — it allocates
  // the pass buffer only on first carve, and most chunks never touch a cave.
  // Skipping the sweep is exactly equivalent to reading `solid` everywhere.
  const cavePasses = caveSampler.sampleVolume(worldMinY, surfaces);
  if (cavePasses) {
    for (let y = 0; y < CHUNK_SIZE; y += 1) {
      for (let z = 0; z < CHUNK_SIZE; z += 1) {
        for (let x = 0; x < CHUNK_SIZE; x += 1) {
          const index = blockIndex(x, y, z);
          const columnIndex = x + z * CHUNK_SIZE;
          if (cliffColumns[columnIndex] === 1) continue;
          const cavePass = cavePasses[index] ?? CAVE_PASS_CODE.solid;
          // Entrance connectors are a world invariant. They win even if a future
          // prefab is authored too close; all other cave passes preserve stamps.
          if (cavePass === CAVE_PASS_CODE.entrance
            || (cavePass !== CAVE_PASS_CODE.solid && protectedBlocks[index] !== 1)) {
            chunk.set(x, y, z, AIR);
          }
        }
      }
    }
  }
  // Vegetation lands after the cave carve so nothing can hollow it out, and
  // before the light pass so blades and canopies get direct sky.
  //
  // The references are dominated by this: the meadow shot is mostly grass
  // blades by screen area, and a bare surface reads as a golf course however
  // well it is coloured. `vegetation.ts` had all of this written and nothing
  // called it.
  const biomeAt = (worldX: number, worldZ: number): BiomeId =>
    sampleTerrain(normalizedSeed, worldX, worldZ, graph).biome;

  /** Memoised per column: the cave query is the same for every y in a column. */
  const entranceColumns = new Int8Array(CHUNK_SIZE * CHUNK_SIZE).fill(-1);
  const entranceColumn = (x: number, z: number): boolean => {
    const columnIndex = x + z * CHUNK_SIZE;
    const cached = entranceColumns[columnIndex];
    if (cached !== -1) return cached === 1;
    const surface = litSurfaces[columnIndex] ?? 0;
    const isEntrance = caveSampler.passAt(worldMinX + x, surface, worldMinZ + z, surface) === "entrance";
    entranceColumns[columnIndex] = isEntrance ? 1 : 0;
    return isEntrance;
  };

  for (let z = 0; z < CHUNK_SIZE; z += 1) {
    for (let x = 0; x < CHUNK_SIZE; x += 1) {
      const columnIndex = x + z * CHUNK_SIZE;
      if (cliffColumns[columnIndex] === 1) continue;
      const surface = litSurfaces[columnIndex] ?? 0;
      if (surface <= SEA_LEVEL) continue;
      const worldX = worldMinX + x;
      const worldZ = worldMinZ + z;
      // Blades root in the surface block, so a column the caves opened up has
      // nothing to grow from.
      const rootY = surface - worldMinY;
      if (rootY >= 0 && rootY < CHUNK_SIZE && chunk.get(x, rootY, z) === AIR) continue;
      // An entrance shaft is a world invariant — it has to stay walk-in-able.
      // Grass planted across its mouth seals the only way into the cave
      // network, which no amount of looking at the surface would reveal.
      if (entranceColumn(x, z)) continue;
      // Nothing takes root on bare rock, in the channel, or under water.
      if (rockColumns[columnIndex] === 1) continue;
      const aboveSurface = surface + 1 - worldMinY;
      if (aboveSurface >= 0 && aboveSurface < CHUNK_SIZE && chunk.get(x, aboveSurface, z) === WATER) continue;
      const biome = biomeAt(worldX, worldZ);
      const bladeHeight = groundCoverHeight(normalizedSeed, worldX, worldZ, biome);
      for (let blade = 1; blade <= bladeHeight; blade += 1) {
        const y = surface + blade - worldMinY;
        if (y < 0 || y >= CHUNK_SIZE) continue;
        if (chunk.get(x, y, z) !== AIR) break;
        chunk.set(x, y, z, WORLDGEN_BLOCK_ID.grassBlade);
      }
      const flower = flowerAt(normalizedSeed, worldX, worldZ, biome);
      if (flower !== null) {
        // Recolour the top of the blade rather than adding a voxel above it, so a
        // bloom cannot float over a blade the loop above cut short against
        // something solid.
        const head = flowerHeadHeight(normalizedSeed, worldX, worldZ, bladeHeight);
        for (let offset = 0; offset < head; offset += 1) {
          const y = surface + bladeHeight - offset - worldMinY;
          if (y < 0 || y >= CHUNK_SIZE) continue;
          if (chunk.get(x, y, z) !== WORLDGEN_BLOCK_ID.grassBlade) break;
          chunk.set(x, y, z, flower);
        }
      }
    }
  }

  /**
   * How far a crown's shadow can land from its trunk. `treesNear` widens by the
   * canopy reach, which is enough to *stamp* a tree but not to shade a chunk it
   * does not overlap — and without this the shadows stop dead at chunk borders.
   */
  const SHADOW_REACH = 96;
  const trees = treesNear(
    normalizedSeed,
    graph,
    worldMinX - SHADOW_REACH,
    worldMinX + CHUNK_SIZE - 1 + SHADOW_REACH,
    worldMinZ - SHADOW_REACH,
    worldMinZ + CHUNK_SIZE - 1 + SHADOW_REACH,
    biomeAt,
  );

  /**
   * Skylight reaching each column, 0-15, with canopy cover taken out.
   *
   * Without this everything is lit as though it stood in the open, and the
   * forest floor is exactly as bright as the meadow — which is the single
   * loudest difference from the references, where the ground under a crown is
   * deep shade and that shade is most of what makes the scene read as real.
   *
   * Computed from the tree list rather than by tracing the voxel grid, because
   * a canopy usually sits in a different sub-chunk from the ground it shades
   * and generation only ever sees one chunk at a time.
   */
  const columnSkylight = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE).fill(15);
  for (let z = 0; z < CHUNK_SIZE; z += 1) {
    for (let x = 0; x < CHUNK_SIZE; x += 1) {
      const worldX = worldMinX + x;
      const worldZ = worldMinZ + z;
      const surface = litSurfaces[x + z * CHUNK_SIZE] ?? 0;
      let cover = 0;
      for (const tree of trees) {
        // Where the crown's shadow actually lands, not what sits directly
        // overhead. Straight-down cover darkens the ground under the tree,
        // which is invisible from anywhere you can stand; an offset shadow is
        // the thing the eye reads as sunlight.
        const drop = Math.max(0, tree.canopyCenterY - surface) / SUN_DIRECTION.y;
        const shadowX = tree.x - SUN_DIRECTION.x * drop;
        const shadowZ = tree.z - SUN_DIRECTION.z * drop;
        // The penumbra widens with the drop: a crown ten metres up throws a
        // much softer edge than one at head height.
        // The crown's real width, lobes included — `canopyRadiusXZ` is only its
        // central mass, so shading by it left the lobes casting nothing.
        const reach = tree.reach + drop * 0.18;
        const distance = Math.hypot(worldX - shadowX, worldZ - shadowZ);
        if (distance > reach) continue;
        cover += 1 - (distance / reach) ** 2;
      }
      // Terrain shadowing itself. Every voxel of a hillside presents the same
      // flat top face, so the normal-dot-sun term gives a broad slope no
      // shading at all and it renders as a flat cut-out. Marching the surface
      // toward the sun is what makes a hill read as a hill.
      let terrainShadow = 0;
      for (let step = SHADOW_STEP; step <= SHADOW_MARCH; step += SHADOW_STEP) {
        const ridge = bandHeight(x + SUN_STEP_X * step, z + SUN_STEP_Z * step);
        if (ridge === undefined) break;
        // How far the blocker rises above the ray at this distance.
        const over = ridge - (surface + step);
        if (over > 0) {
          terrainShadow = Math.max(terrainShadow, Math.min(1, over / 6));
          if (terrainShadow >= 1) break;
        }
      }
      if (terrainShadow > 0) cover += terrainShadow * 1.6;

      if (cover <= 0) continue;
      // Overlapping crowns stack, but never to pitch black: a forest floor in
      // the references still reads green, just several stops down.
      const light = Math.round(15 * Math.max(0.3, Math.exp(-cover * 0.75)));
      columnSkylight[x + z * CHUNK_SIZE] = Math.max(3, Math.min(15, light));
    }
  }

  stampTrees({
    seed: normalizedSeed,
    graph,
    minX: worldMinX,
    minY: worldMinY,
    minZ: worldMinZ,
    step: 1,
    surfaces,
    biomes: [],
    trees,
    place(ix, iy, iz, block): void {
      if (ix < 0 || ix >= CHUNK_SIZE || iy < 0 || iy >= CHUNK_SIZE || iz < 0 || iz >= CHUNK_SIZE) return;
      // Trees are placed on their own lattice and know nothing about caves, so
      // a trunk can land squarely on an entrance shaft and plug it. The
      // entrance is a world invariant: it is the only way into the network.
      if (entranceColumn(ix, iz)) return;
      chunk.set(ix, iy, iz, block);
    },
  }, CHUNK_SIZE);

  // Boulders land on top of everything else: they crush grass rather than
  // growing through it, and they must not plug a cave entrance.
  for (const boulder of bouldersNear(
    normalizedSeed,
    graph,
    worldMinX - MAX_BOULDER_REACH,
    worldMinX + CHUNK_SIZE - 1 + MAX_BOULDER_REACH,
    worldMinZ - MAX_BOULDER_REACH,
    worldMinZ + CHUNK_SIZE - 1 + MAX_BOULDER_REACH,
  )) {
    const reach = Math.max(boulder.radiusXZ, boulder.radiusY) + 1;
    const firstX = Math.max(0, boulder.x - reach - worldMinX);
    const lastX = Math.min(CHUNK_SIZE - 1, boulder.x + reach - worldMinX);
    const firstZ = Math.max(0, boulder.z - reach - worldMinZ);
    const lastZ = Math.min(CHUNK_SIZE - 1, boulder.z + reach - worldMinZ);
    const firstY = Math.max(0, boulder.groundY - reach - worldMinY);
    const lastY = Math.min(CHUNK_SIZE - 1, boulder.groundY + reach - worldMinY);
    for (let y = firstY; y <= lastY; y += 1) {
      for (let z = firstZ; z <= lastZ; z += 1) {
        for (let x = firstX; x <= lastX; x += 1) {
          if (entranceColumn(x, z)) continue;
          if (!boulderOccupies(boulder, worldMinX + x, worldMinY + y, worldMinZ + z)) continue;
          chunk.set(x, y, z, STONE);
        }
      }
    }
  }

  // Structures are stamped after the terrain pass, so finish direct skylight
  // here. This keeps canopy and ruin tops under the same warm sun as terrain;
  // concave areas still receive the renderer's vertex AO contact shading.
  for (let y = 0; y < CHUNK_SIZE; y += 1) {
    for (let z = 0; z < CHUNK_SIZE; z += 1) {
      for (let x = 0; x < CHUNK_SIZE; x += 1) {
        const columnIndex = x + z * CHUNK_SIZE;
        const surface = litSurfaces[columnIndex] ?? 0;
        const sky = (columnSkylight[columnIndex] ?? 15) << 12;
        // Down to the depth erosion can expose, not just the top face. Adjacent
        // columns differ by up to 2 * SURFACE_EROSION_RANGE, so a step's vertical
        // face is made of voxels that far below its own column's surface; lighting
        // only the top leaves every step side on ambient alone, which reads as a
        // black box punched into the ground. Voxels this marks that turn out to
        // be buried are invisible anyway.
        if (worldMinY + y >= surface - (2 * SURFACE_EROSION_RANGE + 1) && chunk.get(x, y, z) !== AIR) {
          // The canopy itself keeps full sun; only what it covers is shaded.
          const lit = worldMinY + y > surface + MAX_GROUND_COVER_HEIGHT ? 0xf000 : sky;
          chunk.setLight(x, y, z, lit);
        }
      }
    }
  }
  return chunk;
}
